import { useEffect, useState } from "react";
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Divider,
  Grid2,
  IconButton,
  Modal,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, ElectricalServices } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  emptyPort,
  Port,
  PortFormErrors,
  PortPayload,
  PORT_OPERATORS,
} from "../types";
import { useModalStyles } from "../../components/modalStyles";
import { validatePort } from "../validation";

interface ModalPortProps {
  open: boolean;
  loading?: boolean;
  portData?: Port | null;
  onClose: () => void;
  onSave: (payload: PortPayload, id?: number) => void;
}

export default function ModalPort(props: ModalPortProps) {
  const { open, loading = false, portData, onClose, onSave } = props;

  const [form, setForm] = useState<PortPayload>(emptyPort);
  const [errors, setErrors] = useState<PortFormErrors>({});

  const theme = useTheme();
  const styles = useModalStyles(theme);
  const isEdit = Boolean(portData?.id);

  useEffect(() => {
    if (!open) return;

    let initialData: PortPayload = emptyPort;

    if (portData) {
      const { id, ...rest } = portData;
      initialData = { ...emptyPort, ...rest };
    }

    setForm(initialData);
    setErrors(validatePort(initialData));
  }, [open, portData]);

  const handleChange = (field: keyof PortPayload, value: string) => {
    const next = { ...form, [field]: value };
    setForm(next);
    setErrors(validatePort(next));
  };

  const hasErrors = Object.keys(errors).length > 0;

  const handleSave = () => {
    const errs = validatePort(form);
    const operatorFormatted =
      form.operator === "="
        ? `'${form.operator.toString().trim()}`
        : form.operator.toString().trim();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    onSave(
      {
        operator: operatorFormatted,
        port: form.port.toString().trim(),
      },
      portData?.id,
    );
  };

  return (
    <Modal open={open} onClose={loading ? () => {} : onClose}>
      <Paper sx={{ ...styles.paper, minHeight: "fit-content" }}>
        <Stack sx={styles.boxHeader}>
          <Stack sx={styles.boxTitle}>
            <Stack
              sx={{
                p: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 1.5,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ElectricalServices color="primary" />
            </Stack>
            <Stack>
              <Typography
                fontWeight="bold"
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: "1.05rem",
                  lineHeight: 1.2,
                }}
              >
                {isEdit ? "Editar porta" : "Adicionar porta"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {form.port ? `${form.operator} ${form.port}` : "Nova porta"}
              </Typography>
            </Stack>
          </Stack>
          <IconButton aria-label="close" onClick={onClose} disabled={loading}>
            <Close />
          </IconButton>
        </Stack>

        <Divider sx={styles.divider} />

        <Grid2 container columnSpacing={1} sx={styles.boxFields}>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <Typography variant="subtitle2">Operador</Typography>
            <Autocomplete
              fullWidth
              size="small"
              options={PORT_OPERATORS}
              value={form.operator || null}
              onChange={(_event, newValue) =>
                handleChange("operator", newValue ?? "")
              }
              isOptionEqualToValue={(option, value) => option === value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Selecione o operador"
                  error={Boolean(errors.operator)}
                  helperText={errors.operator ?? " "}
                />
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 8 }}>
            <Typography variant="subtitle2">Porta</Typography>
            <TextField
              fullWidth
              size="small"
              autoComplete="off"
              placeholder="Digite a porta"
              value={form.port ?? ""}
              onChange={(event) => handleChange("port", event.target.value)}
              error={Boolean(errors.port)}
              helperText={errors.port ?? " "}
            />
          </Grid2>
        </Grid2>

        <Box sx={styles.boxActions}>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            loading={loading}
            disabled={hasErrors || loading}
            onClick={handleSave}
          >
            {isEdit ? "Salvar" : "Adicionar"}
          </LoadingButton>
        </Box>
      </Paper>
    </Modal>
  );
}
